import React, { useState, useEffect, useCallback } from "react";
import {
  ClipboardList, Search, RefreshCw, Loader, Download,
  Calendar, Clock, Users, Shield, UserX,
} from "lucide-react";
import { lmsGetAuditLog, lmsGetLiveClasses, lmsSyncLiveClassStatus } from "../api/lmsApi";
import "../lms.css";

const getSchoolId = () => {
  try {
    const u = JSON.parse(localStorage.getItem("userData") || "{}");
    return u.school_id || u.admin_id || null;
  } catch { return null; }
};

const fmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const duration = (joinedAt, leftAt) => {
  if (!joinedAt || !leftAt) return "—";
  const ms = new Date(leftAt) - new Date(joinedAt);
  if (ms < 0) return "—";
  const m = Math.floor(ms / 60000);
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
};

const ROLE_META = {
  moderator: { label: "Teacher",  bg: "#e0f2fe", color: "#0891b2" },
  attendee:  { label: "Student",  bg: "#dcfce7", color: "#16a34a" },
};

export default function LiveClassAuditLog() {
  const schoolId = getSchoolId();

  const [logs,             setLogs]            = useState([]);
  const [classes,          setClasses]          = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [search,           setSearch]           = useState("");
  const [filterClass,      setFilterClass]      = useState("");
  const [filterRole,       setFilterRole]       = useState("");
  const [filterDate,       setFilterDate]       = useState("");
  const [filterSchoolClass,setFilterSchoolClass]= useState("");
  const [filterBatch,      setFilterBatch]      = useState("");
  const [filterSubject,    setFilterSubject]    = useState("");
  const [page,             setPage]             = useState(1);
  const PAGE_SIZE = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { school_id: schoolId };
      if (filterClass) params.live_class_id = filterClass;
      if (filterRole)  params.role = filterRole;
      if (filterDate)  params.date = filterDate;
      const res = await lmsGetAuditLog(params);
      setLogs(res.data?.data || res.data || []);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [schoolId, filterClass, filterRole, filterDate]);

  useEffect(() => {
    lmsSyncLiveClassStatus().catch(() => {}).finally(() => load());
  }, [load]);
  useEffect(() => { setPage(1); }, [search, filterClass, filterRole, filterDate, filterSchoolClass, filterBatch, filterSubject]);

  useEffect(() => {
    lmsGetLiveClasses(schoolId ? { school_id: schoolId } : {})
      .then((res) => setClasses(res.data?.data || res.data || []))
      .catch(() => {});
  }, [schoolId]);

  // Derive unique school classes / batches / subjects from fetched live classes
  const schoolClassOptions = [...new Map(classes.filter((c) => c.class_id && c.class_name).map((c) => [c.class_id, c])).values()];
  const batchOptions       = [...new Map(classes.filter((c) => c.batch_id  && c.batch_name ).map((c) => [c.batch_id,  c])).values()];
  const subjectOptions     = [...new Map(classes.filter((c) => c.subject_id && c.subject_name).map((c) => [c.subject_id, c])).values()];

  // Build a map of live_class_id → live class object for fast lookup
  const classMap = Object.fromEntries(classes.map((c) => [String(c.id), c]));

  // Client-side filter
  const filtered = logs.filter((l) => {
    if (search) {
      const q = search.toLowerCase();
      if (!(l.user_name || "").toLowerCase().includes(q) && !(l.live_class_title || "").toLowerCase().includes(q)) return false;
    }
    if (filterSchoolClass || filterBatch || filterSubject) {
      const lc = classMap[String(l.live_class_id)];
      if (filterSchoolClass && String(lc?.class_id)   !== String(filterSchoolClass)) return false;
      if (filterBatch       && String(lc?.batch_id)   !== String(filterBatch))       return false;
      if (filterSubject     && String(lc?.subject_id) !== String(filterSubject))     return false;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // CSV export
  const handleExport = () => {
    const header = ["User Name", "Role", "Class Title", "Joined At", "Left At", "Duration", "School ID"];
    const rows = filtered.map((l) => [
      l.user_name || "",
      l.role || "",
      l.live_class_title || "",
      l.joined_at || "",
      l.left_at || "",
      duration(l.joined_at, l.left_at),
      l.school_id || "",
    ]);
    const csv = [header, ...rows].map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Stats
  const totalJoins    = filtered.length;
  const studentJoins  = filtered.filter((l) => l.role === "attendee").length;
  const teacherJoins  = filtered.filter((l) => l.role === "moderator").length;
  const uniqueUsers   = new Set(filtered.map((l) => l.user_id)).size;

  const clearFilters = () => { setSearch(""); setFilterClass(""); setFilterRole(""); setFilterDate(""); setFilterSchoolClass(""); setFilterBatch(""); setFilterSubject(""); };
  const hasAnyFilter = filterClass || filterRole || filterDate || search || filterSchoolClass || filterBatch || filterSubject;

  return (
    <div className="lms-content-inner">

      {/* Page Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 20, color: "#111827" }}>Live Class Audit Log</div>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>Complete record of who joined, when, and for how long</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={load} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "9px 16px",
            borderRadius: 9, border: "1.5px solid #e5e7eb", background: "#fff",
            color: "#6b7280", fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}><RefreshCw size={13} /> Refresh</button>
          <button onClick={handleExport} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "9px 16px",
            borderRadius: 9, border: "none", background: "linear-gradient(135deg,#4f46e5,#06b6d4)",
            color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}><Download size={13} /> Export CSV</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "14px 18px", marginBottom: 16, border: "1px solid #e5e7eb" }}>
        {/* Row 1: search + role + date + clear */}
        <div className="crs-filters" style={{ marginBottom: 10 }}>
          <div className="crs-search-wrap" style={{ flex: 1, minWidth: 180 }}>
            <Search size={14} />
            <input placeholder="Search by name or class..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="crs-filter-select" style={{ flex: "0 0 130px", width: 130 }}>
            <option value="">All Roles</option>
            <option value="attendee">Students</option>
            <option value="moderator">Teachers</option>
          </select>
          <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)}
            className="crs-filter-select" style={{ flex: "0 0 150px", width: 150, backgroundImage: "none" }} />
          {hasAnyFilter && (
            <button onClick={clearFilters} style={{
              padding: "8px 14px", borderRadius: 9, border: "1.5px solid #fee2e2",
              background: "#fff5f5", color: "#dc2626", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
            }}>✕ Clear All</button>
          )}
        </div>

        {/* Row 2: class / batch / subject / live session */}
        <div className="crs-filters" style={{ marginBottom: 0 }}>
          <select value={filterSchoolClass} onChange={(e) => { setFilterSchoolClass(e.target.value); setFilterBatch(""); setFilterSubject(""); }}
            className="crs-filter-select" style={{ flex: "1 1 0", width: 0, minWidth: 120 }}>
            <option value="">All Classes</option>
            {schoolClassOptions.map((c) => (
              <option key={c.class_id} value={String(c.class_id)}>{c.class_name}</option>
            ))}
          </select>
          <select value={filterBatch} onChange={(e) => setFilterBatch(e.target.value)}
            className="crs-filter-select" style={{ flex: "1 1 0", width: 0, minWidth: 120, opacity: filterSchoolClass ? 1 : 0.5 }}
            disabled={!filterSchoolClass}>
            <option value="">All Batches</option>
            {batchOptions.filter((b) => !filterSchoolClass || String(b.class_id) === String(filterSchoolClass)).map((b) => (
              <option key={b.batch_id} value={String(b.batch_id)}>{b.batch_name}</option>
            ))}
          </select>
          <select value={filterSubject} onChange={(e) => setFilterSubject(e.target.value)}
            className="crs-filter-select" style={{ flex: "1 1 0", width: 0, minWidth: 120, opacity: filterSchoolClass ? 1 : 0.5 }}
            disabled={!filterSchoolClass}>
            <option value="">All Subjects</option>
            {subjectOptions.filter((s) => !filterSchoolClass || String(s.class_id) === String(filterSchoolClass)).map((s) => (
              <option key={s.subject_id} value={String(s.subject_id)}>{s.subject_name}</option>
            ))}
          </select>
          <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)}
            className="crs-filter-select" style={{ flex: "1 1 0", width: 0, minWidth: 150 }}>
            <option value="">All Live Sessions</option>
            {classes
              .filter((c) => (!filterSchoolClass || String(c.class_id) === String(filterSchoolClass)) &&
                             (!filterBatch   || String(c.batch_id)   === String(filterBatch)) &&
                             (!filterSubject || String(c.subject_id) === String(filterSubject)))
              .map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 16 }}>
        {[
          { label: "Total Joins",  value: totalJoins,   color: "#4f46e5", icon: ClipboardList },
          { label: "Students",     value: studentJoins, color: "#16a34a", icon: Users },
          { label: "Teachers",     value: teacherJoins, color: "#0891b2", icon: Shield },
          { label: "Unique Users", value: uniqueUsers,  color: "#f59e0b", icon: UserX },
        ].map(({ label, value, color, icon: Icon }) => (
          <div key={label} style={{
            background: "#fff", borderRadius: 12, padding: "16px 18px",
            border: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 12,
          }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon size={18} color={color} />
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#111827" }}>{value}</div>
              <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>{label.toUpperCase()}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{
        background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden",
      }}>
        {/* Table header */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr 2fr 1.5fr 1.5fr 1fr",
          padding: "10px 18px",
          background: "#f8fafc", borderBottom: "1px solid #e5e7eb",
          fontSize: 11, fontWeight: 700, color: "#6b7280", letterSpacing: "0.04em",
        }}>
          <span>USER</span>
          <span>ROLE</span>
          <span>LIVE CLASS</span>
          <span>JOINED AT</span>
          <span>LEFT AT</span>
          <span>DURATION</span>
        </div>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
            <Loader size={28} className="ms2-spin" color="#4f46e5" />
          </div>
        ) : paginated.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}>
            <ClipboardList size={40} color="#e5e7eb" style={{ margin: "0 auto 12px" }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: "#6b7280" }}>No audit records found</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Records appear when participants join live classes</div>
          </div>
        ) : (
          paginated.map((l, i) => {
            const role = ROLE_META[l.role] || ROLE_META.attendee;
            return (
              <div
                key={l.id || i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 2fr 1.5fr 1.5fr 1fr",
                  padding: "12px 18px",
                  borderBottom: "1px solid #f1f5f9",
                  alignItems: "center",
                  background: i % 2 === 0 ? "#fff" : "#fafafa",
                }}
              >
                {/* User */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                    background: role.bg, color: role.color,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 700,
                  }}>
                    {(l.user_name || "?")[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                      {l.user_name || "Unknown"}
                    </div>
                    {l.ip_address && (
                      <div style={{ fontSize: 10, color: "#9ca3af" }}>{l.ip_address}</div>
                    )}
                  </div>
                </div>

                {/* Role badge */}
                <div>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 8,
                    background: role.bg, color: role.color,
                  }}>
                    {role.label}
                  </span>
                </div>

                {/* Class */}
                <div style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>
                  {l.live_class_title || `Class #${l.live_class_id}`}
                </div>

                {/* Joined */}
                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#6b7280" }}>
                  <Calendar size={11} />
                  {fmt(l.joined_at)}
                </div>

                {/* Left */}
                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#6b7280" }}>
                  {l.left_at ? (
                    <><Calendar size={11} />{fmt(l.left_at)}</>
                  ) : (
                    <span style={{ color: "#16a34a", fontWeight: 600, fontSize: 11 }}>● Still in class</span>
                  )}
                </div>

                {/* Duration */}
                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#374151", fontWeight: 600 }}>
                  <Clock size={11} color="#9ca3af" />
                  {duration(l.joined_at, l.left_at)}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 16 }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: "6px 14px", borderRadius: 8, border: "1.5px solid #e5e7eb",
              background: "#fff", fontSize: 13, fontWeight: 600, cursor: page === 1 ? "not-allowed" : "pointer",
              color: page === 1 ? "#d1d5db" : "#374151",
            }}
          >← Prev</button>

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((n) => n === 1 || n === totalPages || Math.abs(n - page) <= 2)
            .reduce((acc, n, idx, arr) => {
              if (idx > 0 && n - arr[idx - 1] > 1) acc.push("...");
              acc.push(n);
              return acc;
            }, [])
            .map((n, i) =>
              n === "..." ? (
                <span key={`dots-${i}`} style={{ color: "#9ca3af", padding: "0 4px" }}>…</span>
              ) : (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  style={{
                    padding: "6px 12px", borderRadius: 8, border: "1.5px solid",
                    borderColor: page === n ? "#4f46e5" : "#e5e7eb",
                    background: page === n ? "#4f46e5" : "#fff",
                    color: page === n ? "#fff" : "#374151",
                    fontSize: 13, fontWeight: 600, cursor: "pointer",
                  }}
                >{n}</button>
              )
            )}

          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: "6px 14px", borderRadius: 8, border: "1.5px solid #e5e7eb",
              background: "#fff", fontSize: 13, fontWeight: 600,
              cursor: page === totalPages ? "not-allowed" : "pointer",
              color: page === totalPages ? "#d1d5db" : "#374151",
            }}
          >Next →</button>

          <span style={{ fontSize: 12, color: "#9ca3af", marginLeft: 8 }}>
            {filtered.length} records · Page {page} of {totalPages}
          </span>
        </div>
      )}
    </div>
  );
}
